import { Control, Controller, FieldPath, FieldValues, useController } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FormLabel, Text, TextInput } from "@oysterhr/ui"
import classNames from "classnames"
import { useCurrencyConversionFactor } from "./hooks/useCurrencyConversionFactor"
import { ChangeEvent } from "react"
import { convertToMaxDecimals, CurrencyCode, getCurrencyByCode } from "@oysterhr/common"
import { IconWithTooltip } from "@oysterhr/feature-components"

export interface Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  officialCurrency: string
  approximateCurrency: string
  officialFieldName: TName
  approximateFieldName: TName
  getCurrencyConversionFactor?: (
    officialCurrency: string,
    approximateCurrency: string,
  ) => Promise<number>
  disabled?: boolean
  disableApproximate?: boolean
}

export const DoubleMoneyInput = <TFieldValues extends FieldValues>({
  control,
  officialCurrency,
  approximateCurrency,
  officialFieldName,
  approximateFieldName,
  getCurrencyConversionFactor,
  disabled,
  disableApproximate,
}: Props<TFieldValues>) => {
  const { t } = useTranslation("components/DoubleMoneyInput")
  const {
    field: { onChange: setOfficialValue },
  } = useController({ name: officialFieldName, control })
  const {
    field: { onChange: setApproximateValue },
  } = useController({ name: approximateFieldName, control })
  const { toOfficial, toApproximate } = useCurrencyConversionFactor(
    officialCurrency,
    approximateCurrency,
    getCurrencyConversionFactor,
  )
  const [officialCurrencyObj, approximateCurrencyObj] = getCurrencyByCode([
    officialCurrency,
    approximateCurrency,
  ] as CurrencyCode[])

  const onChange = (fn: (value: string) => void) => (event: ChangeEvent<HTMLInputElement>) => {
    const currency =
      event.target.name === officialFieldName ? officialCurrencyObj : approximateCurrencyObj
    fn(
      convertToMaxDecimals(
        event.target.value.replace(/[^\-0-9.]/g, ""),
        currency?.currencyDecimalDigits ?? 2,
      ),
    )
  }

  const onOfficialChange = (value: string) => {
    setApproximateValue(toApproximate(value))
    setOfficialValue(value)
  }
  const onApproximateChange = (value: string) => {
    setOfficialValue(toOfficial(value))
    setApproximateValue(value)
  }

  return (
    <div
      className={classNames("overflow-hidden rounded-lg border-2 bg-white", {
        "bg-gray-100": disabled,
      })}
    >
      <div className="p-2 pb-2" data-testid="doublemoney-amount">
        <Controller
          control={control}
          name={officialFieldName}
          render={({ field: { value, ...field }, fieldState: { error } }) => (
            <TextInput
              {...field}
              value={value || ""}
              wrapperClassName="border-0 px-1"
              placeholder="00.00"
              size="sm"
              onChange={onChange(onOfficialChange)}
              startAdornment={officialCurrency}
              disabled={disabled}
              label={
                <FormLabel
                  className="px-1 text-gray-400"
                  label={
                    <Text size="small">{t("official.label", { currency: officialCurrency })}</Text>
                  }
                  endAdornment={<IconWithTooltip size="xs" value={t("official.tooltip")} />}
                />
              }
              error={error?.message}
            />
          )}
        />
      </div>
      <hr className="h-0 border-t-2" />
      <div
        className={classNames("p-2 pb-2", { "bg-gray-100": disableApproximate })}
        data-testid="doublemoney-approximate"
      >
        <Controller
          control={control}
          name={approximateFieldName}
          render={({ field: { value, ...field }, fieldState: { error } }) => (
            <TextInput
              {...field}
              value={value || ""}
              wrapperClassName="border-0 px-1"
              placeholder="00.00"
              size="sm"
              onChange={onChange(onApproximateChange)}
              startAdornment={approximateCurrency}
              disabled={disabled || disableApproximate}
              label={
                <FormLabel
                  className="px-1 text-gray-400"
                  label={
                    <Text size="small">
                      {t("approximate.label", { currency: approximateCurrency })}
                    </Text>
                  }
                  endAdornment={<IconWithTooltip size="xs" value={t("approximate.tooltip")} />}
                />
              }
              error={error?.message}
            />
          )}
        />
      </div>
    </div>
  )
}
